import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATS = ["faq", "service_description", "troubleshooting", "pricing", "policy", "service_area", "website_package", "automation_package", "promotion"];
const STATUS = ["draft", "approved", "archived"];
const VIS = ["public", "customer_portal", "internal", "owner"];
const empty = { title: "", category: "faq", content: "", status: "draft", visibility: "public", tags: [] };
const badge = { approved: "bg-green-100 text-green-700", draft: "bg-amber-100 text-amber-700", archived: "bg-slate-100 text-slate-500" };

export default function KnowledgeBase() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: items } = useQuery({ queryKey: ["knowledge"], queryFn: () => api.get("/knowledge").then(r => r.data.items) });
  const del = async (id) => { try { await api.delete(`/knowledge/${id}`); qc.invalidateQueries({ queryKey: ["knowledge"] }); toast.success("Deleted"); } catch (e) { toast.error("Failed"); } };
  return (
    <div>
      <PageTitle title="Knowledge Base" subtitle="Only approved, public items are used by Nexus AI" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setEditing({ ...empty })} data-testid="new-kb-btn"><Plus className="h-4 w-4" /> New Entry</Button>
      } />
      <div className="grid md:grid-cols-2 gap-4">
        {(items || []).map((k) => (
          <div key={k.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`kb-item-${k.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 flex-wrap"><h3 className="font-heading font-semibold text-navy">{k.title}</h3><span className={`text-[10px] px-1.5 py-0.5 rounded ${badge[k.status]}`}>{k.status}</span></div>
                <p className="text-xs text-slate-400 font-mono mt-0.5">{k.category} · {k.visibility} · v{k.version}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(k)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-kb-${k.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(k.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600 line-clamp-3">{k.content}</p>
          </div>
        ))}
      </div>
      {editing && <KbDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["knowledge"] }); setEditing(null); }} />}
    </div>
  );
}

function KbDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...item });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    const payload = { title: form.title, category: form.category, content: form.content, status: form.status, visibility: form.visibility, tags: form.tags || [] };
    try { if (item.id) await api.put(`/knowledge/${item.id}`, payload); else await api.post("/knowledge", payload); toast.success("Saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{item.id ? "Edit" : "New"} Knowledge</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="kb-form">
        <input className={input} placeholder="Title" required value={form.title} onChange={e => set("title", e.target.value)} data-testid="kb-title" />
        <div className="grid grid-cols-3 gap-2">
          <select className={input} value={form.category} onChange={e => set("category", e.target.value)}>{CATS.map(c => <option key={c}>{c}</option>)}</select>
          <select className={input} value={form.status} onChange={e => set("status", e.target.value)} data-testid="kb-status">{STATUS.map(s => <option key={s}>{s}</option>)}</select>
          <select className={input} value={form.visibility} onChange={e => set("visibility", e.target.value)}>{VIS.map(v => <option key={v}>{v}</option>)}</select>
        </div>
        <textarea className={`${input} h-auto py-2`} rows={5} placeholder="Content" required value={form.content} onChange={e => set("content", e.target.value)} data-testid="kb-content" />
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="kb-save">Save</Button>
      </form>
    </DialogContent></Dialog>
  );
}
